import { getPreviewTag } from '@clofo/data/lib'

/**
 * The dev-only logged-in placeholder page (see pages/dev-logged-in.tsx), used as the post-login
 * landing page in local dev where no destination app is served.
 */
export const DEV_LOGGED_IN_URL = '/#/dev-logged-in'

/**
 * Decoded `next` param carried in the login URL, or null when absent. Mirrors the legacy login
 * app: `next` is a base64-encoded path (set by `http.redirectToLoginWithNextUrl()` as
 * `/apps/login#/?next=<base64>`).
 */
const getNextDestination = () => {
  const raw = window.location.href.match(/next=([^&#]*)/)?.[1]
  if (!raw) return null
  try {
    return window.atob(raw)
  } catch {
    // Malformed/un-decodable next — fall through to the default destination.
    console.warn('Ignoring malformed `next` redirect param:', raw)
    return null
  }
}

/**
 * Default destination when the login URL carries no `next` — the overview app. When this build is
 * itself a PR preview, the same preview of overview, matching the links posted on the PR, so a
 * preview login session stays inside the preview.
 */
const getDefaultDestination = () => {
  const previewTag = getPreviewTag()
  if (previewTag) {
    return `/react/${previewTag}/overview/index.html?reactTag=${previewTag}`
  }
  return '/apps/overview'
}

/**
 * Where to navigate after a successful login. Same as production, except in local dev with no
 * explicit `next`: no destination app runs on the login dev server (its SPA fallback would bounce
 * the user straight back to this login page), so we land on the logged-in placeholder instead.
 */
export const getRedirectTo = () => {
  const next = getNextDestination()
  if (next) return next
  if (import.meta.env.DEV) return DEV_LOGGED_IN_URL
  return getDefaultDestination()
}
